import React from 'react';
import { X, Scale } from 'lucide-react';

interface RegulationModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const RegulationModal: React.FC<RegulationModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div
                className="bg-white dark:bg-[#121214] w-full max-w-2xl rounded-3xl shadow-2xl border border-white/20 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                            <Scale className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h2 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-violet-600 dark:from-blue-400 dark:to-violet-400">
                            Regulamento
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[70vh] overflow-y-auto custom-scrollbar text-sm text-gray-600 dark:text-gray-300 space-y-6 leading-relaxed">

                    <section>
                        <h3 className="font-bold text-gray-900 dark:text-white mb-2 text-base">1. Sobre o Radar da Economia</h3>
                        <p>O Radar da Economia é uma plataforma digital que divulga ofertas, cupons de desconto e oportunidades promocionais de lojas parceiras.</p>
                        <p className="mt-2">O site utiliza links de afiliados, o que significa que pode receber comissão quando o usuário realiza compras por meio dos links divulgados.</p>
                        <p className="mt-2">Essa monetização permite que o Radar da Economia continue oferecendo conteúdos gratuitos e benefícios exclusivos aos usuários.</p>
                    </section>

                    <section>
                        <h3 className="font-bold text-gray-900 dark:text-white mb-2 text-base">2. Funcionamento dos Links</h3>
                        <p>Os links disponibilizados no site podem conter identificadores de rastreamento.</p>
                        <p className="mt-2">Ao acessar uma loja por meio desses links, o usuário pode gerar comissão para o Radar da Economia sem qualquer custo adicional.</p>
                        <p className="mt-2">O preço final do produto não é alterado por conta do uso do link afiliado.</p>
                    </section>

                    <section>
                        <h3 className="font-bold text-gray-900 dark:text-white mb-2 text-base">3. Sobre Ofertas e Cupons</h3>
                        <p>O Radar da Economia:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li>Divulga ofertas públicas e/ou promocionais;</li>
                            <li>Pode disponibilizar cupons próprios ou de parceiros;</li>
                            <li>Pode oferecer benefícios exclusivos, quando aplicável.</li>
                        </ul>
                        <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg border border-yellow-200 dark:border-yellow-900/30">
                            <p className="font-semibold text-yellow-800 dark:text-yellow-500 mb-1">Importante:</p>
                            <ul className="list-disc pl-5 space-y-1 text-yellow-700 dark:text-yellow-400">
                                <li>Não garantimos disponibilidade, estoque ou validade das ofertas.</li>
                                <li>Os preços e condições podem ser alterados pelas lojas sem aviso prévio.</li>
                                <li>A aplicação do cupom depende das regras definidas pela loja parceira.</li>
                            </ul>
                        </div>
                    </section>

                    <section>
                        <h3 className="font-bold text-gray-900 dark:text-white mb-2 text-base">4. Benefícios Exclusivos</h3>
                        <p>Eventualmente, o Radar da Economia poderá oferecer benefícios como:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li>Programa de Níveis e Pontos (fidelidade);</li>
                            <li>Acesso antecipado a promoções;</li>
                            <li>Cupons exclusivos;</li>
                            <li>Vantagens personalizadas.</li>
                        </ul>
                        <p className="mt-2 font-medium">Esses benefícios:</p>
                        <ul className="list-disc pl-5 mt-1 space-y-1">
                            <li>Estão sujeitos às regras específicas de cada campanha;</li>
                            <li>Podem exigir cadastro ou validação;</li>
                            <li>Podem ser suspensos em caso de uso indevido ou tentativa de fraude.</li>
                        </ul>
                    </section>

                    <section>
                        <h3 className="font-bold text-gray-900 dark:text-white mb-2 text-base">5. Responsabilidade</h3>
                        <p>O Radar da Economia:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li>Não vende produtos diretamente;</li>
                            <li>Não é responsável por entrega, garantia, troca ou suporte;</li>
                            <li>Atua exclusivamente como divulgador de ofertas.</li>
                        </ul>
                        <p className="mt-2 font-medium">Toda compra é realizada diretamente com a loja parceira.</p>
                    </section>

                    <section>
                        <h3 className="font-bold text-gray-900 dark:text-white mb-2 text-base">6. Uso Indevido</h3>
                        <p className="font-medium text-red-500 dark:text-red-400">É proibido:</p>
                        <ul className="list-disc pl-5 mt-1 space-y-1">
                            <li>Manipular sistemas de rastreamento;</li>
                            <li>Criar contas falsas para obtenção de benefícios;</li>
                            <li>Utilizar métodos automatizados para gerar comissões indevidas.</li>
                        </ul>
                        <p className="mt-2 font-medium text-red-500 dark:text-red-400">O descumprimento pode resultar em:</p>
                        <ul className="list-disc pl-5 mt-1 space-y-1">
                            <li>Suspensão de benefícios;</li>
                            <li>Bloqueio de conta;</li>
                            <li>Cancelamento de vantagens acumuladas.</li>
                        </ul>
                    </section>

                    <section>
                        <h3 className="font-bold text-gray-900 dark:text-white mb-2 text-base">7. Privacidade e Dados</h3>
                        <p>O uso do site implica concordância com a Política de Privacidade.</p>
                        <p className="mt-2">Podemos utilizar cookies e tecnologias similares para:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li>Melhorar a experiência do usuário;</li>
                            <li>Medir performance dos links;</li>
                            <li>Otimizar ofertas.</li>
                        </ul>
                    </section>

                    <section>
                        <h3 className="font-bold text-gray-900 dark:text-white mb-2 text-base">8. Alterações no Regulamento</h3>
                        <p>O Radar da Economia poderá alterar este regulamento a qualquer momento, sendo recomendada a consulta periódica desta página.</p>
                    </section>

                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-medium hover:opacity-90 transition-opacity"
                    >
                        Entendido
                    </button>
                </div>
            </div>
        </div>
    );
};
